import type { ReactNode } from 'react'
import Image from 'next/image'
import Link from 'next/link'

type ErrorPageShellProps = {
    code: string
    title: string
    message: string
    note: string
    /** "4xx" uses red badge; "5xx" uses amber badge */
    variant: '4xx' | '5xx'
    actions?: ReactNode
}

/**
 * Branded error layout ported from the retail site (`cloudaxe.io` ErrorPageShell
 * and `/error/*.html`). Two-column content + floating logo.
 *
 * Inputs: HTTP-style `code`, heading `title`, body `message`/`note`, badge
 * `variant`, optional action buttons.
 * Output: full-viewport branded error UI. Call from `not-found.tsx` / `error.tsx`.
 */
export function ErrorPageShell({
    code,
    title,
    message,
    note,
    variant,
    actions,
}: ErrorPageShellProps) {
    const badgeClass =
        variant === '4xx'
            ? 'border border-red-200 bg-red-50 text-red-700'
            : 'border border-amber-200 bg-amber-50 text-amber-700'

    return (
        <div className="flex min-h-screen bg-white text-gray-900">
            <div className="flex flex-[1_1_66.666%] flex-col justify-center px-8 py-12 sm:px-12">
                <div className="mx-auto w-full max-w-md">
                    <div className="mb-8 flex flex-col items-center gap-3 sm:flex-row sm:items-center">
                        <Image
                            src="/images/cloudaxe_onlyLogo_Transparent_NoBuffer.png"
                            alt="CloudAxe"
                            width={48}
                            height={48}
                            className="h-12 w-12 shrink-0 object-contain"
                            priority
                        />
                        <span className="text-xl font-semibold tracking-tight text-black sm:text-2xl">
                            CloudAxe
                        </span>
                    </div>

                    <span
                        className={`mb-4 inline-block rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wider ${badgeClass}`}
                    >
                        Error {code}
                    </span>

                    <h1 className="mb-4 text-3xl font-bold leading-tight text-gray-900 sm:text-4xl">
                        {title}
                    </h1>
                    <h2 className="mb-3 text-base font-medium text-gray-700">
                        The following error occurred:
                    </h2>
                    <p className="mb-3 text-[0.9375rem] leading-relaxed text-gray-600">
                        {message}
                    </p>
                    <p className="text-sm leading-relaxed text-gray-500">{note}</p>

                    {actions ? <div className="mt-8 flex flex-wrap gap-3">{actions}</div> : null}

                    <div className="mt-10 border-t border-gray-200 pt-6 text-center text-[0.8125rem] text-gray-400">
                        <p>
                            Hosted by{' '}
                            <Link
                                href="/"
                                className="font-medium text-blue-900 no-underline hover:underline visited:text-blue-600"
                            >
                                CloudAxe
                            </Link>
                        </p>
                    </div>
                </div>
            </div>

            <aside
                className="relative hidden flex-[0_0_33.333%] overflow-hidden rounded-l-3xl bg-white lg:flex"
                aria-hidden="true"
            >
                <div className="relative flex min-h-[400px] w-full items-center justify-center overflow-hidden">
                    <Image
                        src="/images/cloudaxe_onlyLogo_Transparent_NoBuffer.png"
                        alt=""
                        width={220}
                        height={220}
                        className="h-[220px] w-[220px] object-contain opacity-60 animate-[float-slow_24s_ease-in-out_infinite]"
                        priority
                    />
                </div>
            </aside>
        </div>
    )
}
